import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Download } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { Button } from "@/shared/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/shared/components/ui/tabs";
import { AcceptDeclineActions } from "@/shared/components/EstateItems/AcceptDeclineActions";
import { trackEvent, EventType } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { AddEstateItemModal } from "@/shared/components/EstateItems/AddEstateItemModal";
import { generateInventoryPdf } from "@/shared/utils/generateInventoryPdf";
import { CategoryEditDialog } from "@/shared/components/EstateItems/CategoryEditDialog";
import { SUPPORTED_CATEGORIES } from "@/shared/components/EstateItems/categoryEditHelpers";
import { EditEstateItemModal } from "@/shared/components/EstateItems/EditEstateItemModal";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";

import type { EstateItem } from "@/shared/types/estateTypes";

import { findLinkedSecuredDebts, findLinkedProperty } from "./utils/estateItemUtils";

import { OverviewCard } from "./components/OverviewCard";
import { CategoryCard } from "./components/CategoryCard";
import { EstateAccountTab } from "./components/EstateAccountTab";
import { EstateItemSheet } from "@/shared/components/EstateItems/EstateItemSheet";
import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";

const MARKED_AS_NONE = "__MARKED_AS_NONE__";

interface CategoryGroup {
  title: string;
  classification: "asset" | "debt";
  moduleId?: string;
  reviewScreen?: string;
  subCategories: string[];
  defaultVisible?: boolean;
}

const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    title: "Real Estate",
    classification: "asset",
    defaultVisible: true,
    moduleId: "real-estate",
    subCategories: ["Properties"],
  },
  {
    title: "Financial Accounts",
    classification: "asset",
    defaultVisible: true,
    moduleId: "financial-accounts",
    reviewScreen: "review_accounts",
    subCategories: [
      "Bank accounts",
      "Brokerage accounts",
      "Tax-advantaged savings",
      "Retirement accounts",
      "Crypto accounts",
      "NFTs",
      "Other digital assets",
    ],
  },
  {
    title: "Insurance",
    classification: "asset",
    defaultVisible: true,
    moduleId: "benefits-eligibility",
    reviewScreen: "life_insurance_review",
    subCategories: ["Life insurance", "Long-term care insurance", "Annuity contracts"],
  },
  {
    title: "Tangible Personal Property",
    classification: "asset",
    defaultVisible: true,
    moduleId: "personal-property",
    subCategories: ["Vehicles", "Valuables", "Safe or safety deposit box"],
  },
  {
    title: "Business Interests",
    classification: "asset",
    subCategories: [
      "LLC or partnership interest",
      "Stock in a private company",
      "Sole proprietorship",
    ],
  },
  {
    title: "Other Assets",
    classification: "asset",
    defaultVisible: true,
    moduleId: "other-assets",
    subCategories: [
      "Receivables",
      "Work-related pay and shares",
      "Royalties and Intellectual Property",
      "Prepaid Services and Credits",
    ],
  },
  {
    title: "Debts and Liabilities",
    classification: "debt",
    defaultVisible: true,
    moduleId: "debts-loans",
    reviewScreen: "debts_loans_review",
    subCategories: ["Unsecured Debts", "Secured debts"],
  },
  {
    title: "Ongoing Estate Expenses",
    classification: "debt",
    defaultVisible: true,
    moduleId: "expenses",
    subCategories: ["Ongoing Estate Expenses"],
  },
  {
    title: "Other Debts",
    classification: "debt",
    subCategories: ["Contingent Liabilities", "Tax Liabilities"],
  },
];

export const EstateInventoryPage: FC = () => {
  const navigate = useNavigate();
  const { person, user } = useAppContext();
  const { tier } = usePaymentTier(person?.estate_id);
  const isPremium = hasMinTier(tier, "basic");
  const { deceasedUserId } = useDeceasedUserData();
  const {
    assets,
    debts,
    summary,
    isFetching,
    updateItem,
    isUpdatingItem,
    deleteItem,
    isDeletingItem,
    itemActions: { handleAccept: acceptItem, handleDecline: declineAction, getItemLoadingAction },
  } = useEstateItemActions(deceasedUserId);

  const [isDownloading, setIsDownloading] = useState(false);
  const [processedIds, setProcessedIds] = useState<Set<number>>(new Set());
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [addingToCategory, setAddingToCategory] = useState<string | null>(null);

  const handleDownloadPdf = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    const startTime = Date.now();
    try {
      await generateInventoryPdf({ assets, debts, summary });
      trackEvent(EventType.ESTATE_INVENTORY_DOWNLOADED, { estate_id: person?.estate_id });
      const durationMs = Date.now() - startTime;
      sentryUtils.logUserAction(
        {
          actionName: "estate_inventory_pdf_download",
          durationMs,
          success: true,
        },
        { userId: user?.user_id, estateId: person?.estate_id },
      );
    } catch (err) {
      const durationMs = Date.now() - startTime;
      sentryUtils.logUserAction(
        {
          actionName: "estate_inventory_pdf_download",
          durationMs,
          success: false,
          errorMessage: err instanceof Error ? err.message : "Unknown error",
        },
        { userId: user?.user_id, estateId: person?.estate_id },
      );
      sentryUtils.captureError(err, "PDF generation failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const allItems = useMemo(() => [...assets, ...debts], [assets, debts]);

  const handleAcceptWithTracking = useCallback(
    async (id: number) => {
      const ok = await acceptItem(id);
      if (ok) setProcessedIds((prev) => new Set(prev).add(id));
    },
    [acceptItem],
  );

  const handleDeclineWithTracking = useCallback(
    async (id: number) => {
      const ok = await declineAction(id);
      if (ok) setProcessedIds((prev) => new Set(prev).add(id));
    },
    [declineAction],
  );

  const [editingItem, setEditingItem] = useState<EstateItem | null>(null);

  const renderItemActions = useCallback(
    (item: EstateItem) => {
      if (
        !item.extracted_from_document ||
        item.extraction_status !== "pending" ||
        processedIds.has(item.id)
      ) {
        return null;
      }
      return (
        <AcceptDeclineActions
          onAccept={() => handleAcceptWithTracking(item.id)}
          onDecline={() => handleDeclineWithTracking(item.id)}
          onEdit={() => setEditingItem(item)}
          loadingAction={getItemLoadingAction(item.id)}
          size="sm"
        />
      );
    },
    [handleAcceptWithTracking, handleDeclineWithTracking, getItemLoadingAction, processedIds],
  );

  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<{
    id: number;
    name: string;
    createdAt: string;
  } | null>(null);
  const selectedItem = useMemo(
    () => (selectedItemId ? (allItems.find((i) => i.id === selectedItemId) ?? null) : null),
    [selectedItemId, allItems],
  );

  const selectedLinkedDebts = useMemo(
    () => (selectedItem ? findLinkedSecuredDebts(selectedItem, allItems) : []),
    [selectedItem, allItems],
  );

  const selectedLinkedProperty = useMemo(
    () => (selectedItem ? findLinkedProperty(selectedItem, allItems) : null),
    [selectedItem, allItems],
  );

  const estateId = person?.estate_id;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "inventory";
  const itemParam = searchParams.get("item");
  const handledItemParamRef = useRef<string | null>(null);

  useEffect(() => {
    if (itemParam && summary && !isFetching && handledItemParamRef.current !== itemParam) {
      handledItemParamRef.current = itemParam;
      const id = Number(itemParam);
      if (allItems.some((i) => i.id === id)) {
        setSelectedItemId(id);
      } else {
        enqueueSnackbar("This item was deleted. Your other assets and debts are still here.", {
          variant: "info",
        });
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("item");
          return next;
        },
        { replace: true },
      );
    }
  }, [itemParam, summary, isFetching, allItems, setSearchParams]);

  return (
    <MainLayout actions={<PageActions />} className="flex flex-1 flex-col">
      <Tabs
        value={activeTab}
        onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}
        className="flex flex-1 flex-col"
      >
        <TabsList hideMobileDropdown>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="estate-account">Estate account</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-0">
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h2>Assets &amp; debts</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleDownloadPdf} disabled={isDownloading}>
                  <Download className="h-4 w-4" />
                  {isDownloading ? "Downloading..." : "Download PDF"}
                </Button>
              </div>
            </div>
            <div className="mt-10 flex flex-col gap-6">
              <OverviewCard />
              <div className="mt-4 flex items-center justify-between">
                <p className="text-lg font-medium">Full Breakdown</p>
                <Button
                  onClick={() => setShowAllCategories((prev) => !prev)}
                  variant="link"
                  className="gap-1 text-sm font-medium text-brand"
                >
                  {showAllCategories ? "Collapse categories" : "Show all categories"}
                  <ChevronsUpDown className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-col gap-4">
                {CATEGORY_GROUPS.map((group) => {
                  const groupItems = allItems.filter((item) =>
                    group.subCategories.includes(item.category),
                  );
                  const hasRealItems = groupItems.some((item) => item.notes !== MARKED_AS_NONE);
                  if (!showAllCategories && !group.defaultVisible && !hasRealItems) return null;
                  return (
                    <CategoryCard
                      key={group.title}
                      title={group.title}
                      classification={group.classification}
                      subCategories={group.subCategories}
                      items={groupItems}
                      showEmpty={showAllCategories}
                      onAddItem={
                        estateId
                          ? (category) => {
                              if (group.moduleId) {
                                navigate(`/${group.moduleId}`, {
                                  state: { returnTo: POST_LOSS_ROUTES.ESTATE_INVENTORY },
                                });
                              } else {
                                setAddingToCategory(category);
                              }
                            }
                          : undefined
                      }
                      onSelectItem={(item) => setSelectedItemId(item.id)}
                      renderItemActions={renderItemActions}
                      allItems={allItems}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="estate-account" className="mt-0 flex flex-1">
          <EstateAccountTab isPaidUser={isPremium} onUpgrade={() => setShowPaywall(true)} />
        </TabsContent>
      </Tabs>

      <EstateItemSheet
        item={selectedItem}
        estateId={person?.estate_id}
        onClose={() => setSelectedItemId(null)}
        onUpdateItem={updateItem}
        onDeleteItem={deleteItem}
        onDocumentClick={(doc) => {
          setSelectedItemId(null);
          setViewingDoc(doc);
        }}
        linkedSecuredDebts={selectedLinkedDebts}
        linkedProperty={selectedLinkedProperty}
        onSelectItem={setSelectedItemId}
        isPaidUser={isPremium}
        onUpgrade={() => setShowPaywall(true)}
      />
      <PostLossPaywall
        isOpen={showPaywall}
        onOpenChange={setShowPaywall}
        promptLocation="estate_inventory"
      />

      {viewingDoc && (
        <DocumentSheet
          isOpen
          setIsOpen={(open) => {
            if (!open) setViewingDoc(null);
          }}
          documentId={viewingDoc.id}
          documentName={viewingDoc.name}
          documentCreatedAt={new Date(viewingDoc.createdAt)}
          estateId={person?.estate_id}
        />
      )}

      {editingItem && SUPPORTED_CATEGORIES.has(editingItem.category) ? (
        <CategoryEditDialog
          key={editingItem.id}
          item={editingItem}
          isOpen
          onClose={() => setEditingItem(null)}
          updateItem={updateItem}
          isUpdatingItem={isUpdatingItem}
          deleteItem={deleteItem}
          isDeletingItem={isDeletingItem}
        />
      ) : editingItem ? (
        <EditEstateItemModal
          key={editingItem.id}
          isOpen
          onClose={() => setEditingItem(null)}
          estateId={editingItem.estate_id}
          item={editingItem}
        />
      ) : null}

      {addingToCategory && estateId && (
        <AddEstateItemModal
          key={addingToCategory}
          isOpen
          onClose={() => setAddingToCategory(null)}
          estateId={estateId}
          initialCategory={addingToCategory}
        />
      )}
    </MainLayout>
  );
};
